import Rating from '@material-ui/lab/Rating';
import cx from 'classnames';
import Button from 'components/atoms/Button';
import BottomSheet from 'components/molecules/BottomSheet';
import Separator from 'components/molecules/Separator';
import { BUTTON_NAMES } from 'constant/button-names';
import { useRateOrder } from 'pages/queries/order.queries';
import React from 'react';
import Form from 'react-bootstrap/Form';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import styles from './styles.module.scss';

const StyledSkip = styled.div`
  padding-left: 4px;
  padding-top: 4px;
  font-size: 14px;
  font-weight: 400;
  opacity: 0.7;
`;

interface IssueTagBoxProps {
  onSelected?: () => any;
  isSelected?: boolean;
  title?: string;
}

const StyledIssueTagBox = styled.div``;

const IssueTagBox: React.FC<IssueTagBoxProps> = ({ isSelected = false, onSelected = () => {}, title }) => {
  const handleSelected = () => {
    onSelected();
  };
  return (
    <StyledIssueTagBox
      className={cx(styles.issueTagBox, {
        [styles.selected]: isSelected,
      })}
      onClick={handleSelected}
    >
      <h4 className={cx('mb-0', styles.title)}>{title}</h4>
    </StyledIssueTagBox>
  );
};

interface Props {
  orderId: string;
  show?: boolean;
  onClose?: (type?: string) => any;
}

const RateOrderSheet: React.FC<Props> = ({ orderId, show = false, onClose = () => {} }) => {
  const [sheetMode, setSheetMode] = React.useState<'starRating' | 'feedback'>('starRating');
  const [starRating, setStarRating] = React.useState(-1);
  const [issueTagsList, setIssueTagsList] = React.useState<string[]>([]);
  const [feedback, setFeedback] = React.useState('');
  const { mutate: rateOrder } = useRateOrder();

  const submitRating = () => {
    rateOrder({
      orderId,
      data: {
        starRating,
        issueTagsList: setIssueTagsList.length > 0 ? issueTagsList : undefined,
        feedback: feedback || undefined,
      },
    });
    onClose();
  };

  const handleOnSubmitClicked = () => {
    if (sheetMode === 'starRating' && starRating === 5) {
      submitRating();
    } else if (sheetMode === 'starRating' && starRating <= 4) {
      setSheetMode('feedback');
    } else if (sheetMode === 'feedback') {
      submitRating();
    }
  };

  const handleFeedbackChange: (event: React.ChangeEvent<HTMLInputElement>) => void = ({ target }) => {
    setFeedback(target.value);
  };

  const handleIssueTagClick = (key: string) => {
    setIssueTagsList(([...currentTagsList]) => {
      if (currentTagsList.includes(key)) {
        currentTagsList.splice(currentTagsList.indexOf(key), 1);
      } else {
        currentTagsList.push(key);
      }
      return currentTagsList;
    });
  };

  const handleOnSkipClicked = () => {
    onClose();
  };

  const handleRatingChange: (event: React.ChangeEvent<{}>, value: number | null) => void = (event, number) => {
    setStarRating(number || -1);
  };

  const isSubmitBtnDisabled = React.useMemo(
    () => (sheetMode === 'starRating' && starRating < 1) || (sheetMode === 'feedback' && !feedback && issueTagsList.length === 0),
    [sheetMode, starRating, feedback, issueTagsList.length]
  );
  const { t } = useTranslation();
  const starRatingTextsObjectT: { [key: string]: string } = t('common.starRatingTexts', { returnObjects: true });
  const starRatingTexts = [
    starRatingTextsObjectT.notGreat,
    starRatingTextsObjectT.couldBeBetter,
    starRatingTextsObjectT.good,
    starRatingTextsObjectT.veryGood,
    starRatingTextsObjectT.excellent,
  ];
  const issueTagOptionsObjectT: { [key: string]: string } = t('common.issueTagOptions', { returnObjects: true });
  return (
    <BottomSheet show={show} onClose={onClose}>
      <div className={cx('d-flex flex-column justify-content-center align-items-center')}>
        <h3>{starRating < 1 ? t('common.lastOrderReviewQuestion') : starRatingTexts[starRating - 1]}</h3>
        <Rating name="orderRating" size="large" readOnly={sheetMode === 'feedback'} onChange={handleRatingChange} />
        {sheetMode === 'feedback' && (
          <div>
            <Separator sm />
            <h3 className="text-center mb-4">{t('common.feedbackQuestion')}</h3>
            <div className="d-flex flex-wrap justify-content-between">
              {Object.keys(issueTagOptionsObjectT).map((issueKey) => (
                <IssueTagBox
                  key={issueKey}
                  title={issueTagOptionsObjectT[issueKey]}
                  isSelected={issueTagsList.includes(issueKey)}
                  onSelected={() => handleIssueTagClick(issueKey)}
                />
              ))}
            </div>
            <div className="mb-4">
              <h4 className="font-weight-normal mt-4">{t('common.feedbackNotesQuestion')}</h4>
              <Form.Control as="textarea" placeholder={t('common.max200Characters')} maxLength={400} id="feedback" onChange={handleFeedbackChange} />
            </div>
          </div>
        )}
        <Button
          block
          size="lg"
          className="mt-6"
          type="submit"
          disabled={isSubmitBtnDisabled}
          onClick={handleOnSubmitClicked}
          buttonName={BUTTON_NAMES.SUBMIT}
        >
          {t('common.submit')}
        </Button>
        <StyledSkip className="mt-2" onClick={handleOnSkipClicked}>
          {t('common.skip')}
        </StyledSkip>
      </div>
    </BottomSheet>
  );
};

export default RateOrderSheet;
